import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

const STATUS_STYLE = {
  'scheduled':   { bg: '#F1EFE8', color: '#5F5E5A' },
  'confirmed':   { bg: '#E6F1FB', color: '#185FA5' },
  'in_progress': { bg: '#FAEEDA', color: '#854F0B' },
  'completed':   { bg: '#EAF3DE', color: '#3B6D11' },
  'cancelled':   { bg: '#FCEBEB', color: '#A32D2D' },
  'no_access':   { bg: '#FCEBEB', color: '#A32D2D' },
}

export default function Dispatch() {
  const navigate = useNavigate()
  const [dispatches, setDispatches] = useState([])
  const [parts, setParts] = useState([])
  const [weekOffset, setWeekOffset] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchAll() }, [weekOffset])

  async function fetchAll() {
    const { start, end } = weekRange(weekOffset)
    const [dispRes, partsRes] = await Promise.all([
      supabase.from('dispatches')
        .select('*, sites(id, name, city), programs(slug, color), parts_commitments(quantity, parts_inventory(name, sku))')
        .gte('scheduled_date', start.toISOString().slice(0,10))
        .lte('scheduled_date', end.toISOString().slice(0,10))
        .order('scheduled_date'),
      supabase.from('parts_inventory').select('*, parts_commitments(quantity, used_quantity)').order('name'),
    ])
    setDispatches(dispRes.data || [])
    setParts(partsRes.data || [])
    setLoading(false)
  }

  if (loading) return <div className="spinner" />

  const { start, end, days } = weekRange(weekOffset)
  const byDay = {}
  days.forEach(d => { byDay[d.toDateString()] = [] })
  dispatches.forEach(d => {
    const key = new Date(d.scheduled_date).toDateString()
    if (byDay[key]) byDay[key].push(d)
  })

  const totalHours = dispatches.reduce((a, d) => a + Number(d.estimated_hours || 0), 0)
  const techs = new Set(dispatches.map(d => d.technician_name || d.technician_id).filter(Boolean))

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dispatch Board</div>
          <div className="page-subtitle">
            Week of {start.toLocaleDateString('en-US',{month:'short',day:'numeric'})} – {end.toLocaleDateString('en-US',{month:'short',day:'numeric'})}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" onClick={() => setWeekOffset(weekOffset - 1)}>‹ Prev</button>
          <button className="btn btn-sm" onClick={() => setWeekOffset(0)}>This week</button>
          <button className="btn btn-sm" onClick={() => setWeekOffset(weekOffset + 1)}>Next ›</button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-number">{dispatches.length}</div><div className="stat-label">Visits this week</div></div>
        <div className="stat-card"><div className="stat-number">{techs.size}</div><div className="stat-label">Technicians</div></div>
        <div className="stat-card"><div className="stat-number">{totalHours.toFixed(0)}h</div><div className="stat-label">Est. hours</div></div>
        <div className="stat-card"><div className="stat-number" style={{ color: 'var(--green)' }}>{dispatches.filter(d => d.status === 'completed').length}</div><div className="stat-label">Completed</div></div>
      </div>

      {/* Day columns */}
      {days.map(day => {
        const key = day.toDateString()
        const list = byDay[key] || []
        const dayName = day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        return (
          <div key={key} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text)' }}>
                {dayName}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{list.length} {list.length === 1 ? 'visit' : 'visits'}</div>
            </div>
            {list.length === 0 ? (
              <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 6, fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
                No visits
              </div>
            ) : list.map(d => {
              const ss = STATUS_STYLE[d.status] || STATUS_STYLE.scheduled
              return (
                <div key={d.id} className="card" style={{ marginBottom: 6, padding: 12 }}
                  onClick={() => navigate(`/sites/${d.sites?.id || ''}`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{d.sites?.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        {d.technician_name || 'Unassigned'} · {d.estimated_hours}h
                        {d.parts_commitments?.length > 0 && ` · ${d.parts_commitments.length} parts`}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 11, padding: '3px 9px', borderRadius: 10,
                      background: ss.bg, color: ss.color, fontWeight: 500, flexShrink: 0,
                      textTransform: 'capitalize', alignSelf: 'start',
                    }}>
                      {d.status.replace('_',' ')}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}

      {/* Parts inventory */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Parts staging</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Inventory committed to upcoming dispatches
        </div>
        {parts.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 12 }}>
            No parts inventory loaded yet.
          </div>
        ) : parts.map(p => {
          const committed = (p.parts_commitments || []).reduce((a, c) => a + (c.quantity || 0), 0)
          const available = (p.staged_qty || 0) - committed
          const healthy = available >= 2
          return (
            <div key={p.id} style={{
              display: 'flex', justifyContent: 'space-between', padding: '10px 0',
              borderBottom: '1px solid var(--border)', fontSize: 13,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{p.sku}</div>
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 13, alignItems: 'center' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Staged</div>
                  <div>{p.staged_qty}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Committed</div>
                  <div>{committed}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Available</div>
                  <div style={{ fontWeight: 600, color: healthy ? 'var(--green)' : 'var(--amber)' }}>{available}</div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function weekRange(offset = 0) {
  const now = new Date()
  const day = now.getDay() // 0=Sun, 1=Mon...
  const mondayOffset = day === 0 ? -6 : 1 - day
  const monday = new Date(now)
  monday.setDate(now.getDate() + mondayOffset + (offset * 7))
  monday.setHours(0, 0, 0, 0)
  const friday = new Date(monday)
  friday.setDate(monday.getDate() + 4)
  friday.setHours(23, 59, 59, 999)
  const days = []
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    days.push(d)
  }
  return { start: monday, end: friday, days }
}
